import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Search, User, Check, Upload, X } from "lucide-react";
import { db, storage, auth } from "@/firebase/config";
import { collection, addDoc, query, where, getDocs, limit, serverTimestamp, doc, runTransaction } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { IncidentSeverity, IncidentTier } from "./types";
import { calculateNewScore, determineTier } from "./logic";

interface IncidentReporterProps {
  schoolId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface StudentSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  registrationNumber: string;
  className: string;
  photoUrl?: string; // We'll need to fetch this from student record
}

export const IncidentReporter = ({ schoolId, onSuccess, onCancel }: IncidentReporterProps) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Student Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<StudentSearchResult[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<StudentSearchResult | null>(null);

  // Step 2: Categorization
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState<IncidentSeverity>('minor');
  const [tier, setTier] = useState<IncidentTier>(1);
  const [location, setLocation] = useState("");
  const [incidentDate, setIncidentDate] = useState(new Date().toISOString().slice(0, 16)); // DateTime local format

  // Step 3: Details & Evidence
  const [description, setDescription] = useState("");
  const [witnesses, setWitnesses] = useState<{name: string, role: string}[]>([]);
  const [newWitnessName, setNewWitnessName] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState<File[]>([]);

  // Search Logic
  useEffect(() => {
    const searchStudents = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }
      
      setSearching(true);
      try {
        const studentsRef = collection(db, `schools/${schoolId}/students`);
        // Basic prefix search - in real app might use Algolia or specialized index
        // Firestore lacks native full-text search, so this is simplistic
        const q = query(studentsRef, where("firstName", ">=", searchQuery), where("firstName", "<=", searchQuery + '\uf8ff'), limit(5));
        const snapshot = await getDocs(q);
        
        const results = snapshot.docs.map(doc => ({
          id: doc.id,
          firstName: doc.data().firstName,
          lastName: doc.data().lastName,
          registrationNumber: doc.data().registrationNumber,
          className: doc.data().className || doc.data().class || 'N/A',
          photoUrl: doc.data().photoUrl
        }));
        setSearchResults(results);
      } catch (err) {
        console.error("Search failed", err);
      } finally {
        setSearching(false);
      }
    };

    const debounce = setTimeout(searchStudents, 500);
    return () => clearTimeout(debounce);
  }, [searchQuery, schoolId]);

  const handleNext = () => setStep(prev => prev + 1);
  const handleBack = () => setStep(prev => prev - 1);

  const handleSubmit = async () => {
    if (!selectedStudent) return;
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      // 1. Upload Evidence
      const evidenceUrls: string[] = [];
      for (const file of evidenceFiles) {
        const fileRef = ref(storage, `schools/${schoolId}/incidents/${selectedStudent.id}/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        const url = await getDownloadURL(fileRef);
        evidenceUrls.push(url);
      }

      // 2. Transaction: Create Incident AND Update Student
      await runTransaction(db, async (transaction) => {
          // Get fresh student data for accurate scoring
          const studentRef = doc(db, `schools/${schoolId}/students`, selectedStudent.id);
          const studentDoc = await transaction.get(studentRef);
          if (!studentDoc.exists()) throw "Student not found";

          const currentScore = studentDoc.data().behaviorScore ?? 100;
          const currentTier = studentDoc.data().behaviorTier ?? 1;

          // Calculate new stats
          // Assuming all reporter incidents are 'demerits' for now unless we add a 'Type' toggle
          const newScore = calculateNewScore(currentScore, 'demerit', severity);
          const newTier = determineTier(newScore, currentTier, severity === 'critical');

          // Create Incident Ref
          const newIncidentRef = doc(collection(db, `schools/${schoolId}/disciplineIncidents`));

          // Set Incident
          transaction.set(newIncidentRef, {
            studentId: selectedStudent.id,
            studentName: `${selectedStudent.firstName} ${selectedStudent.lastName}`,
            studentRegNum: selectedStudent.registrationNumber,
            reporterId: auth.currentUser!.uid,
            reporterName: auth.currentUser!.displayName || "Staff Member", 
            type: 'behavior', 
            severity,
            tier, // Incident tier vs Student Tier might differ, keeping Incident input for now
            category,
            location,
            timestamp: new Date(incidentDate).toISOString(),
            description,
            witnesses,
            evidenceUrls,
            status: severity === 'minor' ? 'new' : 'investigating',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          // Update Student
          transaction.update(studentRef, {
              behaviorScore: newScore,
              behaviorTier: newTier,
              lastIncidentDate: new Date(incidentDate).toISOString()
          });
      });

      toast({
        title: "Incident Reported",
        description: `Case logged. ${selectedStudent.firstName}'s score updated.`,
      });
      onSuccess();
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Error",
        description: "Failed to submit report. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const addWitness = () => {
    if (newWitnessName.trim()) {
      setWitnesses([...witnesses, { name: newWitnessName, role: 'student' }]);
      setNewWitnessName("");
    }
  };

  return (
    <Card className="w-full max-w-3xl mx-auto border-0 shadow-lg">
      <CardHeader>
        <CardTitle>Report New Incident</CardTitle>
        <CardDescription>Step {step} of 3</CardDescription>
        <div className="w-full bg-slate-100 h-2 rounded-full mt-2">
            <div className={`h-full bg-blue-600 rounded-full transition-all duration-300`} style={{ width: `${(step/3)*100}%` }}></div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        
        {/* STEP 1: SELECT STUDENT */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
                <Label>Identify Involvement</Label>
                <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input 
                        placeholder="Search student by name..." 
                        className="pl-9"
                        value={searchQuery} 
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {selectedStudent ? (
                <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-200 flex items-center justify-center overflow-hidden">
                             {selectedStudent.photoUrl ? <img src={selectedStudent.photoUrl} className="h-full w-full object-cover" /> : <User className="h-5 w-5 text-blue-700" />}
                        </div>
                        <div>
                            <p className="font-medium text-blue-900 dark:text-blue-100">{selectedStudent.firstName} {selectedStudent.lastName}</p>
                            <p className="text-xs text-blue-700 dark:text-blue-300">{selectedStudent.registrationNumber} • {selectedStudent.className}</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedStudent(null)}><X className="h-4 w-4" /></Button>
                </div>
            ) : (
                <div className="space-y-2">
                    {searching ? (
                        <div className="flex items-center justify-center py-4"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
                    ) : (
                        searchResults.map(student => (
                            <div key={student.id} 
                                className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer border border-transparent hover:border-slate-200"
                                onClick={() => setSelectedStudent(student)}
                            >
                                <div className="flex items-center gap-3">
                                   <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                                        {student.photoUrl ? <img src={student.photoUrl} className="h-full w-full object-cover" /> : <span className="font-bold text-xs">{student.firstName[0]}</span>}
                                   </div>
                                   <div>
                                       <p className="font-medium text-sm">{student.firstName} {student.lastName}</p>
                                       <p className="text-xs text-gray-500">{student.registrationNumber}</p>
                                   </div>
                                </div>
                                <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-600">{student.className}</span>
                            </div>
                        ))
                    )}
                    {searchQuery.length > 2 && searchResults.length === 0 && !searching && (
                        <p className="text-center text-sm text-muted-foreground py-4">No students found.</p>
                    )}
                </div>
            )}
          </div>
        )}

        {/* STEP 2: CATEGORIZATION */}
        {step === 2 && (
             <div className="space-y-4">
                 <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <Label>Date & Time</Label>
                        <Input type="datetime-local" value={incidentDate} onChange={e => setIncidentDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Location</Label>
                        <Select onValueChange={setLocation} value={location}>
                            <SelectTrigger>
                                <SelectValue placeholder="Where did it happen?" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Classroom">Classroom</SelectItem>
                                <SelectItem value="Hallway">Hallway / Corridor</SelectItem>
                                <SelectItem value="Cafeteria">Cafeteria</SelectItem>
                                <SelectItem value="Playground">Playground / Field</SelectItem>
                                <SelectItem value="Bus">School Bus</SelectItem>
                                <SelectItem value="Dormitory">Dormitory</SelectItem>
                                <SelectItem value="OffCampus">Off Campus</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                 </div>

                 <div className="space-y-2">
                     <Label>Category</Label>
                     <Select onValueChange={(val) => {
                         setCategory(val);
                         // Auto-suggest logic
                         if (['fighting', 'bullying', 'theft'].includes(val)) { setSeverity('major'); setTier(2); }
                         if (['drug_possession', 'assault', 'weapon'].includes(val)) { setSeverity('critical'); setTier(3); }
                     }} value={category}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select infractions..." />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="disruption">Class Disruption (Minor)</SelectItem>
                            <SelectItem value="tardiness">Tardiness / Truancy</SelectItem>
                            <SelectItem value="profanity">Inappropriate Language</SelectItem>
                            <SelectItem value="dress_code">Dress Code Violation</SelectItem>
                            <SelectItem value="bullying">Bullying / Harassment (Major)</SelectItem>
                            <SelectItem value="fighting">Physical Altercation (Major)</SelectItem>
                            <SelectItem value="vandalism">Vandalism</SelectItem>
                            <SelectItem value="academic_dishonesty">Academic Dishonesty</SelectItem>
                            <SelectItem value="drug_possession">Substance Possession (Critical)</SelectItem>
                        </SelectContent>
                     </Select>
                 </div>

                 <div className="grid grid-cols-2 gap-4">
                     <div className="space-y-2">
                        <Label>Severity</Label>
                        <Select onValueChange={(val: any) => setSeverity(val)} value={severity}>
                            <SelectTrigger>
                               <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="minor">Minor (Low Impact)</SelectItem>
                                <SelectItem value="major">Major (High Impact)</SelectItem>
                                <SelectItem value="critical">Critical (Severe/Safety)</SelectItem>
                            </SelectContent>
                        </Select>
                     </div>
                     <div className="space-y-2">
                        <Label>Intervention Tier</Label>
                        <Select onValueChange={(val: any) => setTier(Number(val) as IncidentTier)} value={tier.toString()}>
                            <SelectTrigger>
                               <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">Tier 1 (Universal)</SelectItem>
                                <SelectItem value="2">Tier 2 (Targeted)</SelectItem>
                                <SelectItem value="3">Tier 3 (Intensive)</SelectItem>
                            </SelectContent>
                        </Select>
                     </div>
                 </div>
             </div>
        )}

        {/* STEP 3: DETAILS */}
        {step === 3 && (
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label>Detailed Description</Label>
                    <Textarea 
                        placeholder="Describe specific behaviors, context, and immediate actions taken..." 
                        className="h-32" 
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </div>

                <div className="space-y-2">
                    <Label>Witnesses</Label>
                    <div className="flex gap-2">
                        <Input 
                            placeholder="Add witness name..." 
                            value={newWitnessName} 
                            onChange={e => setNewWitnessName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addWitness()}
                        />
                        <Button type="button" variant="outline" onClick={addWitness}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                        {witnesses.map((w, i) => (
                            <div key={i} className="flex items-center gap-1 bg-slate-100 text-xs px-2 py-1 rounded">
                                <span>{w.name}</span>
                                <X className="h-3 w-3 cursor-pointer" onClick={() => setWitnesses(prev => prev.filter((_, idx) => idx !== i))} />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-2">
                    <Label>Evidence (Optional)</Label>
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                        <Input 
                            type="file" 
                            multiple 
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={e => e.target.files && setEvidenceFiles(Array.from(e.target.files))}
                        />
                        <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">Click or drag photos/documents here</p>
                        {evidenceFiles.length > 0 && <p className="text-xs text-blue-600 mt-2">{evidenceFiles.length} file(s) selected</p>}
                    </div>
                </div>
            </div>
        )}

        <div className="flex justify-between pt-4 border-t">
            <Button variant="ghost" onClick={step === 1 ? onCancel : handleBack}>
                {step === 1 ? 'Cancel' : 'Back'}
            </Button>
            
            {step < 3 ? (
                <Button onClick={handleNext} disabled={(step === 1 && !selectedStudent) || (step === 2 && !category)}>
                    Next Step
                </Button>
            ) : (
                <Button onClick={handleSubmit} disabled={loading || !description}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Submit Report
                </Button>
            )}
        </div>

      </CardContent>
    </Card>
  );
};
